import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useSupabaseAuth } from './AuthContext';
import { apiFetch } from '../apiFetch';
import type { Tool } from '../../types/database';

export interface ToolExecutionInput {
  [key: string]: unknown;
}

export interface ToolExecutionContext {
  [key: string]: unknown;
}

export interface ToolsContextType {
  tools: Tool[];
  loading: boolean;
  error: string | null;
  fetchTools: () => Promise<void>;
  executeTool: (
    toolId: string,
    input: ToolExecutionInput,
    context?: ToolExecutionContext
  ) => Promise<unknown>;
}

export const ToolsContext = createContext<ToolsContextType | undefined>(
  undefined
);

export const ToolsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isSignedIn } = useSupabaseAuth();
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTools = useCallback(async () => {
    if (!isSignedIn) {
      setTools([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use the access-filtered API endpoint (backed by get_user_tools RPC)
      const res = await apiFetch('/api/tools');
      if (!res.ok) throw new Error(`Failed to fetch tools: ${res.status}`);
      const payload = await res.json();
      // GET /api/tools returns a plain array
      const data: Tool[] = Array.isArray(payload)
        ? payload
        : (payload?.data ?? []);
      setTools(data);
    } catch (err) {
      console.error('Failed to load tools:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tools');
      setTools([]);
    } finally {
      setLoading(false);
    }
  }, [isSignedIn]);

  const executeTool = useCallback(
    async (
      toolId: string,
      input: ToolExecutionInput,
      context?: ToolExecutionContext
    ): Promise<unknown> => {
      try {
        setError(null);

        const response = await apiFetch(`/api/tools/${toolId}/execute`, {
          method: 'POST',
          body: JSON.stringify({ input, context }),
        });

        if (!response.ok) {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to execute tool');
          } else {
            throw new Error('API endpoint not available');
          }
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('API returned non-JSON response');
        }

        return await response.json();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to execute tool';
        setError(errorMessage);
        throw err;
      }
    },
    []
  );

  useEffect(() => {
    if (isSignedIn) {
      fetchTools();
    } else {
      setTools([]);
      setLoading(false);
    }
  }, [isSignedIn, fetchTools]);

  const value: ToolsContextType = {
    tools,
    loading,
    error,
    fetchTools,
    executeTool,
  };

  return (
    <ToolsContext.Provider value={value}>{children}</ToolsContext.Provider>
  );
};
